import { useSelector } from 'react-redux'
import cn from 'clsx'

// Components
import { Link, Tooltip, IconAddToCollectionOutline, IconAddToCollectionFilled } from '@tracklib/kit'

// Hooks
import { useRequest } from 'modules/requests/hooks'

// Selectors
import { ownCollectionsSelector } from 'selectors'

// Types
import { Collection, Loop, Song, Track, State } from 'types'

import styles from './addtocollectionbutton.css'

type AddToCollectionButtonProps = {
  trackId: number
  className?: string
  loop?: Loop | null
}

const AddToCollectionButton = ({ trackId, className, loop }: AddToCollectionButtonProps): JSX.Element => {
  const ownCollections = useSelector<State, Array<Collection>>(state => ownCollectionsSelector(state))

  const track = useSelector<State, Track>(state => state.entities.tracks[String(trackId)])
  const song = useSelector<State, Song>(state => state.entities.songs[String(track?.song)])
  const userHasCollections = ownCollections && ownCollections.length
  const isInCollection = loop
    ? ownCollections.some(collection =>
        collection.tracks.some(
          ({ id, loop: innerLoop }) =>
            innerLoop && id === trackId && innerLoop.beat_count === loop.beat_count && innerLoop.beat_start === loop.beat_start,
        ),
      )
    : ownCollections.some(collection => collection.tracks.some(({ id, loop: innerLoop }) => id === trackId && innerLoop === null))
  const fetchCollectionsRequest = useRequest('FETCH_COLLECTIONS')

  const query: { trackId: number; loop?: string } = { trackId }
  if (loop) {
    query.loop = `${loop.beat_start}:${loop.beat_count}`
  }
  const title = `Add or remove ${loop ? 'loop' : 'track'} from collection`
  return (
    <div data-tooltip-wrapper>
      <Tooltip>{title}</Tooltip>
      <Link
        datatesting="save_to_collection"
        title={title}
        ariaLabel={title}
        to={userHasCollections ? 'addtrack' : 'collection'}
        query={query}
        modal
        className={cn(styles.addToCollectionButton, className)}
        disabled={[2, 3].includes(song?.state) || fetchCollectionsRequest.isLoading}
      >
        {isInCollection ? <IconAddToCollectionFilled className={styles.icon} /> : <IconAddToCollectionOutline className={styles.icon} />}
      </Link>
    </div>
  )
}

export default AddToCollectionButton
